import { useEffect } from 'react';
import { authService } from 'components/AuthenticationProvider/useAuthenticationEffects.hook';
import { analytics } from 'utils/analytics';
import { log } from 'utils/log';
import { User } from 'types/generated/graphql';
import { CONFIG } from 'utils/config';
import { EventIds } from '../../constants';
import { useChameleonListener } from './AnalyticsIdentity.helpers';

export interface UseAnalyticsIdentityControllerProps {
    isAuthenticated: boolean;
    isAuthenticationLoading: boolean;
    userProfile: User | undefined;
    continueOrRedirectToProfileSurvey: () => void;
}

// This controller waits until authentication Feature Flags are resolved and
// Chameleon has been added to `window` before calling analytics.identify method
// with the users Feature Flag configuration
export function useAnalyticsIdentityController({
    isAuthenticated,
    isAuthenticationLoading,
    userProfile,
    continueOrRedirectToProfileSurvey,
}: UseAnalyticsIdentityControllerProps): void {
    const chameleonIsDefined = useChameleonListener();

    // Analytics
    useEffect(() => {
        async function sendAnalytics() {
            let analyticsProperties = null;

            if (isAuthenticated) {
                if (userProfile?.userId) {
                    try {
                        await authService.checkOrWaitForOngoingAuthentication();
                        analyticsProperties =
                            await authService.getAnalyticsProperties();
                    } catch (error) {
                        log.error(
                            'Error getting analytics properties',
                            EventIds.FailedGetAnalyticsPropertiesError,
                            error,
                        );
                    }
                } else {
                    return;
                }
            }

            const userId = userProfile?.userId ?? null;

            await analytics.identify(userId, analyticsProperties);
            // Let the identify call finish before continuing or redirecting to profile survey
            setTimeout(() => {
                continueOrRedirectToProfileSurvey();
            }, 500);
        }

        if (isAuthenticated && !userProfile?.userId) {
            return;
        }

        // Only check for chameleon on prod, default to true.
        const isChameleonReady =
            CONFIG.VITE_APP_ENV === 'production' ? chameleonIsDefined : true;

        if (!isAuthenticationLoading && isChameleonReady) {
            sendAnalytics();
        }
    }, [
        isAuthenticated,
        isAuthenticationLoading,
        userProfile,
        chameleonIsDefined,
        continueOrRedirectToProfileSurvey,
    ]);
}
